import React, { useState } from "react";
import { ChevronLeft, Sparkles } from "lucide-react";
import { useAura } from "./AuraProvider";

const STEPS = ["industry", "focus", "team_size"];

// The AI Opportunity Finder: three quick taps (industry → focus → team size) instead of typing. The answers go to
// Simmi as one message plus structured details, and she replies with three tailored use-cases.
const OpportunityFinder = ({ message }) => {
  const { send, finishFinder, messages, t } = useAura();
  const f = t.finder;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  // Only the newest finder is live; an older one (or one already answered) shows its summary.
  const latest = [...messages].reverse().find((m) => m.actions?.some((a) => a.type === "opportunity_finder"));
  if (message.finderDone || latest?.id !== message.id) {
    return (
      <div className="flex items-center gap-2 rounded-[12px] border border-white/10 bg-white/[0.04] px-3 py-2 text-[12px] text-white/60">
        <Sparkles className="h-3.5 w-3.5 text-[#06D3DB]" />
        {message.finderDone || f.title}
      </div>
    );
  }

  const field = STEPS[step];
  const pick = (value) => {
    const next = { ...answers, [field]: value };
    setAnswers(next);
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    const label = (k) => f.options[k].find((o) => o.v === next[k]).label;
    const summary = f.summary(label("industry"), label("focus"), label("team_size"));
    finishFinder(message.id, summary);
    send(f.compose(label("industry"), label("focus"), label("team_size")), { finder: next });
  };

  return (
    <div className="w-full overflow-hidden rounded-[14px] border border-[#06D3DB]/30 bg-gradient-to-br from-[#2563EB]/15 to-[#06D3DB]/[0.06] p-3">
      <div className="mb-2 flex items-center gap-2">
        {step > 0 ? (
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            aria-label={f.back}
            className="-ml-1 flex h-6 w-6 items-center justify-center rounded-full text-white/60 hover:bg-white/10 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        ) : (
          <Sparkles className="h-4 w-4 text-[#06D3DB]" />
        )}
        <span className="flex-1 text-[12px] font-semibold uppercase tracking-[1.2px] text-[#06D3DB]">{f.title}</span>
        <span className="flex gap-1" aria-label={f.stepOf(step + 1, STEPS.length)}>
          {STEPS.map((s, i) => (
            <span key={s} className={`h-1.5 rounded-full transition-all ${i <= step ? "w-4 bg-[#06D3DB]" : "w-1.5 bg-white/20"}`} />
          ))}
        </span>
      </div>
      {/* keyed by step, so each question slides in (App.css .simmi-finder-step) */}
      <div key={field} className="simmi-finder-step">
        <p className="mb-2 text-[13.5px] font-medium text-white">{f.questions[field]}</p>
        <div className="flex flex-wrap gap-1.5">
          {f.options[field].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => pick(o.v)}
              className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
                answers[field] === o.v
                  ? "border-[#06D3DB] bg-[#06D3DB]/20 text-white"
                  : "border-white/15 bg-white/[0.04] text-white/85 hover:border-[#06D3DB]/60 hover:text-white"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OpportunityFinder;
